/**
 * Enhanced Header Menu — Dawn 테마 mega-menu 강화
 *
 * Dawn의 기존 <details>/<summary> 구조를 유지하면서
 * Radix NavigationMenu 수준의 UX를 추가합니다.
 *
 * 기존 Dawn 구조를 그대로 사용:
 *   <details class="mega-menu">
 *     <summary>...</summary>
 *     <div class="mega-menu__content">...</div>
 *   </details>
 *
 * 추가되는 기능:
 *   - Hover open/close with configurable delay (300ms default)
 *   - Skip-delay: 이미 메뉴 탐색 중이면 즉시 전환
 *   - Touch-aware: pointerType으로 마우스/터치 분리
 *   - Keyboard: Esc→닫기+trigger 포커스, Arrow→항목 간 이동
 *   - focusout 자동 닫기
 *   - aria-expanded on summary (details native open 속성과 동기화)
 *
 * 사용법 (Liquid에서):
 *   <enhanced-header-menu
 *     delay="300"
 *     skip-delay="300"
 *   >
 *     <!-- 기존 Dawn header-mega-menu.liquid 그대로 -->
 *   </enhanced-header-menu>
 *
 * 또는 기존 <header-menu>를 대체:
 *   customElements.define('header-menu', EnhancedHeaderMenu)
 */

class EnhancedHeaderMenu extends HTMLElement {
  static get observedAttributes() {
    return ['delay', 'skip-delay'];
  }

  constructor() {
    super();

    /** @type {number} ms before opening on hover */
    this._delay = 300;
    /** @type {number} ms window where delay is skipped after a close */
    this._skipDelay = 300;

    /** @type {number|null} */
    this._openTimer = null;
    /** @type {number|null} */
    this._closeTimer = null;
    /** @type {boolean} */
    this._isSkipWindow = false;
    /** @type {number|null} */
    this._skipTimer = null;

    // Bind
    this._onPointerEnter = this._onPointerEnter.bind(this);
    this._onPointerLeave = this._onPointerLeave.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onFocusOut = this._onFocusOut.bind(this);
    this._onSummaryClick = this._onSummaryClick.bind(this);
  }

  /** @returns {HTMLDetailsElement|null} */
  get details() {
    return this.querySelector('details');
  }

  /** @returns {HTMLElement|null} */
  get summary() {
    return this.querySelector('summary');
  }

  /** @returns {HTMLElement|null} */
  get content() {
    return this.querySelector('.mega-menu__content');
  }

  get isOpen() {
    return this.details?.hasAttribute('open') ?? false;
  }

  connectedCallback() {
    const summary = this.summary;
    const details = this.details;
    const content = this.content;

    // Sync aria-expanded with initial state
    if (summary) {
      summary.setAttribute('aria-expanded', String(this.isOpen));
    }

    // Pointer events on summary (trigger area)
    if (summary) {
      summary.addEventListener('pointerenter', this._onPointerEnter);
      summary.addEventListener('pointerleave', this._onPointerLeave);
      summary.addEventListener('click', this._onSummaryClick);
    }

    // Pointer events on content (keep open while hovering dropdown)
    if (content) {
      content.addEventListener('pointerenter', this._onPointerEnter);
      content.addEventListener('pointerleave', this._onPointerLeave);
    }

    // Keyboard & focus on the whole component
    this.addEventListener('keydown', this._onKeyDown);
    this.addEventListener('focusout', this._onFocusOut);
  }

  disconnectedCallback() {
    const summary = this.summary;
    const content = this.content;

    if (summary) {
      summary.removeEventListener('pointerenter', this._onPointerEnter);
      summary.removeEventListener('pointerleave', this._onPointerLeave);
      summary.removeEventListener('click', this._onSummaryClick);
    }
    if (content) {
      content.removeEventListener('pointerenter', this._onPointerEnter);
      content.removeEventListener('pointerleave', this._onPointerLeave);
    }
    this.removeEventListener('keydown', this._onKeyDown);
    this.removeEventListener('focusout', this._onFocusOut);

    this._clearAllTimers();
  }

  attributeChangedCallback(name, _old, value) {
    if (name === 'delay') this._delay = Math.max(0, parseInt(value, 10) || 300);
    if (name === 'skip-delay') this._skipDelay = Math.max(0, parseInt(value, 10) || 300);
  }

  // ── Open / Close ──────────────────────────────────────────

  open() {
    const details = this.details;
    if (!details || this.isOpen) return;

    details.setAttribute('open', '');
    this.summary?.setAttribute('aria-expanded', 'true');

    // Close sibling menus
    this._closeSiblings();

    this.dispatchEvent(new CustomEvent('menu-open', { bubbles: true }));
  }

  close() {
    const details = this.details;
    if (!details || !this.isOpen) return;

    details.removeAttribute('open');
    this.summary?.setAttribute('aria-expanded', 'false');

    // Start skip-delay window
    this._startSkipWindow();

    this.dispatchEvent(new CustomEvent('menu-close', { bubbles: true }));
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  // ── Hover timing ──────────────────────────────────────────

  /** @param {PointerEvent} e */
  _onPointerEnter(e) {
    if (e.pointerType !== 'mouse') return;

    this._clearAllTimers();

    // If a sibling is already open or in skip window, open immediately
    const siblingOpen = this._anySiblingOpen();
    if (this._isSkipWindow || siblingOpen) {
      this.open();
      return;
    }

    this._openTimer = window.setTimeout(() => {
      this.open();
      this._openTimer = null;
    }, 0);
  }

  /** @param {PointerEvent} e */
  _onPointerLeave(e) {
    if (e.pointerType !== 'mouse') return;

    this._clearAllTimers();

    this._closeTimer = window.setTimeout(() => {
      this.close();
      this._closeTimer = null;
    }, this._delay);
  }

  // ── Click (touch + click fallback) ────────────────────────

  /** @param {Event} e */
  _onSummaryClick(e) {
    // On mouse hover-enabled devices, click should toggle.
    // On touch, the default <details> toggle behavior works fine,
    // but we need to sync aria-expanded.
    // We let the native toggle happen, then sync state.
    requestAnimationFrame(() => {
      this.summary?.setAttribute('aria-expanded', String(this.isOpen));
      if (this.isOpen) {
        this._closeSiblings();
      }
    });
  }

  // ── Keyboard ──────────────────────────────────────────────

  /** @param {KeyboardEvent} e */
  _onKeyDown(e) {
    switch (e.key) {
      case 'Escape':
        if (this.isOpen) {
          this.close();
          this.summary?.focus();
          e.preventDefault();
          e.stopPropagation();
        }
        break;

      case 'ArrowDown':
        if (this.isOpen && e.target === this.summary) {
          // Move focus into content
          const firstLink = this.content?.querySelector('a[href], button, [tabindex]:not([tabindex="-1"])');
          if (firstLink) {
            /** @type {HTMLElement} */ (firstLink).focus();
            e.preventDefault();
          }
        }
        break;
    }
  }

  // ── Focus management ──────────────────────────────────────

  /** @param {FocusEvent} e */
  _onFocusOut(e) {
    // If focus moves outside this component, close
    requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) {
        this._clearAllTimers();
        this.close();
      }
    });
  }

  // ── Sibling coordination ──────────────────────────────────

  /** Close all sibling <enhanced-header-menu> or <header-menu> */
  _closeSiblings() {
    const parent = this.parentElement?.closest('.header__inline-menu, nav-menu, nav');
    if (!parent) return;

    const siblings = parent.querySelectorAll('enhanced-header-menu, header-menu');
    for (const sibling of siblings) {
      if (sibling !== this && sibling instanceof EnhancedHeaderMenu) {
        sibling._clearAllTimers();
        sibling.close();
      }
    }
  }

  /** @returns {boolean} Whether any sibling menu is currently open */
  _anySiblingOpen() {
    const parent = this.parentElement?.closest('.header__inline-menu, nav-menu, nav');
    if (!parent) return false;

    const siblings = parent.querySelectorAll('enhanced-header-menu, header-menu');
    for (const sibling of siblings) {
      if (sibling !== this && sibling instanceof EnhancedHeaderMenu && sibling.isOpen) {
        return true;
      }
    }
    return false;
  }

  // ── Timers ────────────────────────────────────────────────

  _clearAllTimers() {
    if (this._openTimer !== null) {
      clearTimeout(this._openTimer);
      this._openTimer = null;
    }
    if (this._closeTimer !== null) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
  }

  _startSkipWindow() {
    this._isSkipWindow = true;
    if (this._skipTimer !== null) clearTimeout(this._skipTimer);
    this._skipTimer = window.setTimeout(() => {
      this._isSkipWindow = false;
      this._skipTimer = null;
    }, this._skipDelay);
  }
}

// ── Register ──────────────────────────────────────────────────

// Dawn이 이미 header-menu를 정의했을 수 있으므로 체크
if (!customElements.get('enhanced-header-menu')) {
  customElements.define('enhanced-header-menu', EnhancedHeaderMenu);
}
